import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { AppRoute } from 'app/app-layout/constants';
import { TokenInterface } from 'app/core/authentication/models/token.model';
import { AuthService } from 'app/core/authentication/services/auth.service';
import { RouterRequest } from 'app/core/browser/models/router-request.model';
import { BrowserService } from 'app/core/browser/services/browser.service';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

/**
 * Prevent unauthorized activating and loading of routes
 * @class AuthenticationGuard
 */
@Injectable()
export class AuthenticationGuard implements CanActivate {

  constructor(private readonly _browserService: BrowserService, private readonly _authService: AuthService) {}

  /**
   * Allows activation only when a non-expired token is present, redirecting to
   * login otherwise. The token lives in the store (populated by APP_INITIALIZER
   * before routing starts), so getToken() emits the current value synchronously;
   * take(1) resolves the guard on that value and completes.
   * @method canActivate
   */
  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this._authService.getToken()
    .pipe(
      take(1),
      map((authToken: TokenInterface) => {
        const isValid: boolean = AuthenticationGuard.isTokenValid(authToken);
        if (!isValid) {
          this._browserService.to(new RouterRequest({ route: AppRoute.login }));
        }
        return isValid;
      }),
    );
  }

  /**
   * True only when a token is present and its exp claim is in the future.
   * exp is a JWT UNIX timestamp in seconds; Date.now() is in milliseconds.
   */
  private static isTokenValid(authToken: TokenInterface): boolean {
    if (!authToken || !authToken.exp) {
      return false;
    }
    return authToken.exp * 1000 > Date.now();
  }
}
